import React, { useEffect, useState, useRef } from "react";
import ReactSelect from "@src/common/ReactSelect";
import { restrictInvalidNumberKeys } from "@src/config/helper";
import { saveMultiTenure,getMultiTenure } from "@src/store/slices/configPanel";
import { useLanguage } from "@src/context/LanguageContext";
import FullPageLoader from "@src/common/FullPageLoader";
import { toast } from "react-toastify";
import { STATUS_SUCCESS,INSURER_STATUS } from "@src/config/constant";
import * as HELPER from "@src/config/helper";
import { Tooltip as TooltipLite } from 'react-tooltip';
import { ADD_KEYS, ADD_ROW, DROPDOWN_KEYS, headersData, INITIAL_LIST, INITIAL_ROWS, MULTI_DROPDOWN_KEYS, NUMBER_KEYS, OPTION_TYPE, REQUIRED_KEYS } from "./config/MultiTenureConfig";
import MultiSelect from "@src/common/MultiSelect";

const DEPRECIATION_RATE_FIELDS = [
  { key: ADD_KEYS.EIGHT, year: 1 },
  { key: ADD_KEYS.NINE, year: 2 },
  { key: ADD_KEYS.TEN, year: 3 },
  { key: ADD_KEYS.ELEVEN, year: 4 },
  { key: ADD_KEYS.TWELVE, year: 5 },
];

const DUPLICATE_COMBINATION_KEYS = [
  ADD_KEYS.ONE,
  ADD_KEYS.TWO,
  ADD_KEYS.FIVE,
  ADD_KEYS.SIX,
];

const FIXED_FIRST_YEAR_DEPRECIATION_RATE = 100;


const MultiTenure: React.FC<any> = (props) =>  {
  const { masterConfig } = props;

  const partner: OPTION_TYPE[] = masterConfig.partner_list;
  const insurerList: OPTION_TYPE[] = masterConfig.insurer_list_with_partner_id;
  const vehicleCategory: OPTION_TYPE[] = masterConfig.vehicle_category;
  const vehicleType: OPTION_TYPE[] = masterConfig.vehicle_type;
  const vehicleUsage: OPTION_TYPE[] = masterConfig.vehicle_usage;
  const vehicleCondition: OPTION_TYPE[] = masterConfig.vehicle_condition;

  const DROPDOWN_DATA_KEYS:Record<string,any> ={
    ONE:partner,
    TWO:insurerList,
    THREE:vehicleCategory,
    FOUR:vehicleType,
    FIVE:vehicleUsage,
    SIX:vehicleCondition,
    SEVEN:masterConfig.tenure_year
  }
  if(!masterConfig.tenure_year){
    DROPDOWN_DATA_KEYS.SEVEN = [{
      value: 1,
      label: 1
    }]
  }

  const [rows, setRows] = useState(INITIAL_LIST);
  const [addRowEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, any>[]>([]);
  const { translations } = useLanguage();
  const allRowsRef = useRef<{ [page: number]: ADD_ROW[] }>({});
  const pageRef = useRef(1);
  const [tooltip, setTooltip] = useState<any>({});
  
  const [initialRowsData, setInitialRowsData] = useState<any>(INITIAL_ROWS);
  

  const currentPage = pageRef.current;

  const createEmptyRow = (): any => ({
   ...INITIAL_ROWS[0],
    [ADD_KEYS.EIGHT]: FIXED_FIRST_YEAR_DEPRECIATION_RATE,
    __is_new: true,
  });

  const getMaxTenureValue = (value: any) => {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) return parsedValue;
    return Number(String(value || "").match(/\d+/)?.[0]) || 0;
  };

  const clearRatesAfterTenure = (row: any, maxTenure: any) => {
    const tenure = getMaxTenureValue(maxTenure);
    if (!tenure) return;

    DEPRECIATION_RATE_FIELDS.forEach(({ key, year }) => {
      if (year > tenure) row[key] = "";
    });
  };

  const clearRateErrorsAfterTenure = (index: number, maxTenure: any) => {
    const tenure = getMaxTenureValue(maxTenure);
    if (!tenure) return;

    setErrors((prev: any) => {
      const rowErrors = prev?.[currentPage]?.[index];
      if (!rowErrors) return prev;

      const updatedRowErrors = { ...rowErrors };
      let hasChange = false;

      DEPRECIATION_RATE_FIELDS.forEach(({ key, year }) => {
        if (year > tenure && updatedRowErrors[key]) {
          delete updatedRowErrors[key];
          hasChange = true;
        }
      });

      if (!hasChange) return prev;

      const updatedPageErrors = { ...prev[currentPage], [index]: updatedRowErrors };
      if (Object.keys(updatedRowErrors).length === 0) {
        delete updatedPageErrors[index];
      }

      return {
        ...prev,
        [currentPage]: updatedPageErrors,
      };
    });
  };

  const isDepreciationRateDisabled = (row: any, year: number) => {
    const maxTenure = getMaxTenureValue(row?.[ADD_KEYS.SEVEN]);
    return row.insurer_status === INSURER_STATUS.INACTIVE || (maxTenure > 0 && year > maxTenure);
  };

  const addRow = () => {
    if (!addRowEnabled) return;
    const updatedRows = [...(rows?.data || []), createEmptyRow()];
    allRowsRef.current[currentPage] = updatedRows;
    setRows((prev: any) => ({
      ...prev,
      data: updatedRows,
    }));
  };

  const deleteNewRow = (index: number) => {
    const targetRow: any = rows?.data?.[index];
    if (!targetRow?.__is_new) return; // allow delete only for newly added rows

    const updatedRows = (rows?.data || []).filter((_: any, i: number) => i !== index);
    allRowsRef.current[currentPage] = updatedRows;
    setRows((prev: any) => ({
      ...prev,
      data: updatedRows,
    }));

    setErrors((prev: any) => {
      const updated = { ...(prev || {}) };
      const pageErrors = updated?.[currentPage];
      if (!pageErrors) return updated;

      const remapped: any = {};
      for (const [rowIndexStr, rowErr] of Object.entries(pageErrors)) {
        const rowIndex = Number(rowIndexStr);
        if (Number.isNaN(rowIndex)) continue;
        if (rowIndex === index) continue;
        remapped[rowIndex > index ? rowIndex - 1 : rowIndex] = rowErr;
      }
      updated[currentPage] = remapped;
      return updated;
    });
  };


  const handleChange = (event: any, key: string, index: number = 0) => {

    const isDropdown = DROPDOWN_KEYS.includes(key);
    let value: any = isDropdown ? event?.value : event?.target?.value;

    if(MULTI_DROPDOWN_KEYS.includes(key)){
      value = event?.map((item: any) => item.value);
    }
    
    const updatedRows = [...rows.data];
    const row: any = { ...updatedRows[index] };
    

    const isValid = handleChangeValidation(key, value, index, row);
    if (!isValid) return;

    if (key === "partner_id") {
      row["insurer_id"] = null;
    }

    row[key] = value;
    if (key === ADD_KEYS.SEVEN) {
      clearRatesAfterTenure(row, value);
      clearRateErrorsAfterTenure(index, value);
    }
    updatedRows[index] = row;

    allRowsRef.current[currentPage] = updatedRows;

    setRows((prev: any) => ({
      ...prev,
      data: updatedRows,
    }));

    if (DUPLICATE_COMBINATION_KEYS.includes(key)) setErrorsOfDuplicatesAcrossPages(currentPage, index);
  };
  const handleChangeValidation = (
    key: string,
    value: any,
    index: number = 0,
    row: Record<string, any>
  ): boolean => {
    const updatedErrors = { ...errors };
    let newValue = NUMBER_KEYS?.includes(key) ? Number(value): value;
   
    if (!updatedErrors[currentPage]) updatedErrors[currentPage] = {};
    if (!updatedErrors[currentPage][index])
      updatedErrors[currentPage][index] = {};

    const setError = (msg: string) => {
      updatedErrors[currentPage][index][key] = msg;
    };

    const clearError = () => { 
      delete updatedErrors[currentPage][index][key];
      if (Object.keys(updatedErrors[currentPage][index]).length === 0) {
        delete updatedErrors[currentPage][index];
      }
    };
    

 
    if(newValue && [ADD_KEYS.EIGHT,ADD_KEYS.NINE,ADD_KEYS.TEN,ADD_KEYS.ELEVEN,ADD_KEYS.TWELVE]?.includes(key)){
      if(newValue < 40 || newValue > 100){
        setError(translations?.MULTI_TENURE?.DEPRECIATION_ERROR);
      }else if(newValue && newValue?.toString()?.includes('.')){
        const isValid = HELPER.isValidDecimal(newValue);
        if(!isValid){
          setError(translations?.commonMsg?.MAX_4D);
        }else{
          clearError();
        }
      }
      else{
        clearError();
      }
    }
    else if(!newValue && [ADD_KEYS.EIGHT,ADD_KEYS.NINE,ADD_KEYS.TEN,ADD_KEYS.ELEVEN,ADD_KEYS.TWELVE]){
      clearError();
    }else if(MULTI_DROPDOWN_KEYS.includes(key)){
      if(newValue?.length === 0){
        setError(translations?.commonMsg?.FIELD_CANNOT_BE_BLANK);
      }else{
        clearError();
      }
    }
    else if(DROPDOWN_KEYS.includes(key)){
      if(!newValue){
        setError(translations?.commonMsg?.REQUIRED);
      }else{
        clearError();
      }
    }

    setErrors(updatedErrors);
    return true;
  };
  const setErrorsOfDuplicatesAcrossPages = (
    currentPage: number,
    index: number
  ) => {
    const result = findExistingCombination(currentPage, index);

    const updatedErrors: any = { ...errors };
    if (!updatedErrors[currentPage]) updatedErrors[currentPage] = {};
    if (!updatedErrors[currentPage][index])
      updatedErrors[currentPage][index] = {};
    if (result) {
      const messageTemplate =
        translations?.MULTI_TENURE?.DUPLICATE_PARTNER_INSURER ||
        translations?.MULTI_TENURE?.DUPLICATE_PARTNER ||
        "";
      const errorMessage = messageTemplate
        .replace("{{row}}", String(result.index + 1))
        .replace("{{page}}", String(result.page));
      updatedErrors[currentPage][index]["duplicate"] = errorMessage;
      
    } else {
      for (let [rowIndex, rowErrors] of Object.entries(
        updatedErrors[currentPage]
      )) {
        let row: any = rowErrors;
        if (row["duplicate"]) {
          delete updatedErrors[currentPage][rowIndex]["duplicate"];
        }
      }
    }

    setErrors(updatedErrors);
  };
  const findExistingCombination = (
    currentPage: number,
    currentIndex: number
  ) => {
    const allData = allRowsRef.current;
    const currentRow = allData?.[currentPage]?.[currentIndex];
    if (!currentRow) return null;

    const normalizeToNumberSet = (value: any) => {
      const values = Array.isArray(value) ? value : [value];
      return new Set(
        values
          .map((v: any) => Number(v))
          .filter((v: any) => !Number.isNaN(v))
      );
    };

    const hasDuplicateKeyValue = (row: any, key: string) => {
      const currentValues = normalizeToNumberSet(currentRow?.[key]);
      const otherValues = normalizeToNumberSet(row?.[key]);

      if (currentValues.size === 0 || otherValues.size === 0) return false;
      return Array.from(currentValues).some((value) => otherValues.has(value));
    };

    const hasAllDuplicateKeyValues = (row: any) =>
      DUPLICATE_COMBINATION_KEYS.every((key) => hasDuplicateKeyValue(row, key));

    for (const [pageStr, rows] of Object.entries(allData)) {
      const page = parseInt(pageStr);
      for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        if (page === currentPage && index === currentIndex) continue;

        if (hasAllDuplicateKeyValues(row)) return { page, index };
      }
    }

    return null;
  };
  const validateRowsBeforeSubmit = (rows: ADD_ROW[]): boolean => {
    const updatedErrors = { ...errors };

    if (!updatedErrors[currentPage]) updatedErrors[currentPage] = {};
    Object.keys(updatedErrors[currentPage]).forEach((rowIndex) => {
      if (updatedErrors[currentPage][rowIndex]?.duplicate) {
        delete updatedErrors[currentPage][rowIndex].duplicate;
      }
    });

    for (let i = 0; i < rows.length; i++) {
      const duplicateResult = findExistingCombination(currentPage, i);
      if (duplicateResult) {
        if (!updatedErrors[currentPage][i]) updatedErrors[currentPage][i] = {};
        const messageTemplate =
          translations?.MULTI_TENURE?.DUPLICATE_PARTNER_INSURER ||
          translations?.MULTI_TENURE?.DUPLICATE_PARTNER ||
          "";
        updatedErrors[currentPage][i].duplicate = messageTemplate
          .replace("{{row}}", String(duplicateResult.index + 1))
          .replace("{{page}}", String(duplicateResult.page));
      }
    }

    let hasEmptyError = Object.values(updatedErrors).some((pageErrors) =>
      Object.values(pageErrors).some(
        (rowErrors) => Object.keys(rowErrors).length > 0
      )
    );

    if (hasEmptyError) {
      setErrors(updatedErrors);
      return false;
    }


    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!updatedErrors[currentPage][i]) updatedErrors[currentPage][i] = {};

      for (const key of REQUIRED_KEYS) {
        const value = row[key];

        if (value === "" || value === null || value === undefined) {
          updatedErrors[currentPage][i][key] =
            translations?.commonMsg?.required;
        }
      }

      for (const { key, year } of DEPRECIATION_RATE_FIELDS) {
        const value = row[key];

        if (
          year !== 1 &&
          !isDepreciationRateDisabled(row, year) &&
          (value === "" || value === null || value === undefined)
        ) {
          updatedErrors[currentPage][i][key] =
            translations?.commonMsg?.required;
        }
      }
    }


    setErrors(updatedErrors);
    hasEmptyError = Object.values(updatedErrors).some((pageErrors) =>
      Object.values(pageErrors).some(
        (rowErrors) => Object.keys(rowErrors).length > 0
      )
    );

    if (hasEmptyError) {
      setErrors(updatedErrors);
      return false;
    }
    return true;
  };
  const fetchData = (params: any) => {
    getMultiTenure(params)
      .then((res: any) => {
        if (res && res.status === STATUS_SUCCESS) {
          let data: any = res || {};
          if(data?.data.length > 0) {
            data = {
              ...data,
              data: data.data.map((row: any) => ({
                ...row,
                [ADD_KEYS.EIGHT]: FIXED_FIRST_YEAR_DEPRECIATION_RATE,
              })),
            };
            allRowsRef.current[pageRef.current] = data?.data;
            setRows(data);
          }
          setInitialRowsData(data);
        }
        setLoading(false);
      })
      .catch((err: Error) => {});
  };
  const handleSubmit = async () => {
    try {
      
      setLoading(true);
      const isValid = validateRowsBeforeSubmit(rows.data);
      if (!isValid) {
        setLoading(false);
        return;
      }
      let initData = initialRowsData?.['data'];
      const changedData = rows?.data;
      let dataToBeSaved:any = HELPER?.getChangedRows(initData,changedData);
      dataToBeSaved = dataToBeSaved.map((row: any) => {
        for (let key in row.newRow) {
          if(NUMBER_KEYS?.includes(key)) {
            row.newRow[key] = typeof row?.newRow?.[key] === 'string' && row?.newRow?.[key]!="" ? Number(row?.newRow?.[key]) : row?.newRow?.[key]
          }
        }
        if (row?.newRow && typeof row.newRow === "object") {
          row.newRow[ADD_KEYS.EIGHT] = FIXED_FIRST_YEAR_DEPRECIATION_RATE;
          for (const k of Object.keys(row.newRow)) {
            if (k.startsWith("__")) delete row.newRow[k];
          }
        }
        return row?.newRow
      })
      if(dataToBeSaved?.length === 0) {
        setLoading(false);
        toast.error(translations?.commonMsg?.NO_CHANGE || translations?.commonMsg?.loading);
        return
      }
      
      const response: any = await saveMultiTenure( {mappings:dataToBeSaved} );
      if (response.status === STATUS_SUCCESS) {
        const savedRows = (rows?.data || []).map((r: any) => ({ ...r, __is_new: false }));
        allRowsRef.current[currentPage] = savedRows;
        setRows((prev: any) => ({ ...prev, data: savedRows }));
        const updatedInitRowsData = { ...initialRowsData };
        updatedInitRowsData.data = savedRows;
        setInitialRowsData(updatedInitRowsData)
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {}
    finally{
      setLoading(false);
    }
  }
  useEffect(() => {
    setLoading(true);
    fetchData({ page_number: currentPage });
  }, []);

  useEffect(() => {
    const hasErrors = Object.keys(errors || {}).length > 0;
    if (hasErrors && rows.data.length > 0) {
      validateRowsBeforeSubmit(rows.data);
    }
  }, [translations]);

  const handleTooltip = (key: string, action: string) => {
    setTooltip(action === "close" ? {} : { [key]: true });
  };

  const HEADERS = headersData(translations);

  return (
    <React.Fragment>
      <FullPageLoader show={loading} />

       <div className="right-fixed-btn">
        <button
          type="button"
          className="btn-line"
          onClick={handleSubmit}
          disabled={loading}
        >
          {translations?.commonMsg?.save}
        </button>

    </div>
      <div className="multi-tenure-outer confing-tab-table">

        <table>
          <thead>
            <tr>
              <th>{HEADERS?.ONE}</th>
              <th>{HEADERS?.TWO}</th>
              <th>{HEADERS?.THREE}</th>
              <th>{HEADERS?.FOUR}</th>
              <th>{HEADERS?.FIVE}</th>
              <th>{HEADERS?.SIX}</th>
              <th>{HEADERS?.SEVEN}</th>
              <th>{HEADERS?.EIGHT}</th>
              <th>{HEADERS?.NINE}</th>
              <th>{HEADERS?.TEN}</th>
              <th>{HEADERS?.ELEVEN}</th>
              <th>{HEADERS?.TWELVE}</th>
              
            </tr>
          </thead>
          <tbody>
            {rows?.data?.map((row: any, index: number) => (
              <tr key={index}>
                
                 <td>
                 <fieldset className="single-select">
                    <div className="material remove-add-row-container">
                      {addRowEnabled && row?.__is_new ? (
                        <span
                          onClick={() => deleteNewRow(index)}
                          className="remove-add-row"
                          title="Remove Row"
                        >
                          <i className="ic-clearclose"></i>
                        </span>
                      ) : (
                        ""
                      )}
                      <ReactSelect
                        options={DROPDOWN_DATA_KEYS?.ONE || []}
                        placeholder={HEADERS?.ONE}
                        className="select-bx-table"
                        classNamePrefix="select-bx-table"
                        value={
                          row?.[ADD_KEYS.ONE]
                            ? DROPDOWN_DATA_KEYS?.ONE
                                ?.find(
                                  (item: any) => +item.value === +row?.[ADD_KEYS.ONE]
                                )
                            : null
                        }
                        handleChange={(e: any) =>
                          handleChange(e, ADD_KEYS.ONE, index)
                        }
                      />
                      {(errors?.[currentPage]?.[index]?.[ADD_KEYS.ONE] || errors?.[currentPage]?.[index]?.duplicate) ? (
                        <span className="error-txt">
                          {(errors?.[currentPage]?.[index]?.[ADD_KEYS.ONE] || errors?.[currentPage]?.[index]?.duplicate)}
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </fieldset>
                </td>

                <td>
                    <fieldset className="multiselect-dropDown">
                    <div className="material d-flex select-box-width-icon">
                      <MultiSelect
                        key={"dropdown"}
                        options={DROPDOWN_DATA_KEYS.TWO?.filter((v: any) => v?.[ADD_KEYS.ONE] == row?.[ADD_KEYS.ONE])|| []}
                        placeholder={HEADERS?.TWO}
                        className="select-bx-table"
                        classNamePrefix="select-bx-table"
                        value={
                          Array.isArray(row?.[ADD_KEYS.TWO]) &&
                          DROPDOWN_DATA_KEYS?.TWO.length
                            ? DROPDOWN_DATA_KEYS?.TWO?.filter((v: any) => v?.[ADD_KEYS.ONE] == row?.[ADD_KEYS.ONE])?.filter((v: any) =>
                                row?.[ADD_KEYS.TWO].includes(v.value)
                              )
                            : []
                        }
                        isSelectAll={true}
                        isClearable={false}
                        onChange={(e: any) =>
                          handleChange(e, ADD_KEYS.TWO, index)
                        }
                        isDisabled={row.insurer_status===INSURER_STATUS.INACTIVE?true:false}
                      />
                      {errors?.[currentPage]?.[index]?.[ADD_KEYS.TWO] ? (
                        <span className="error-txt">
                          {errors?.[currentPage]?.[index]?.[ADD_KEYS.TWO]}
                        </span>
                      ) : (
                        ""
                      )}
                      {
                        row?.[ADD_KEYS.TWO]?.length > 0 && (
                          <i
                          className="ic-info-icon1 cursor-pointer text-gray-500 hover:text-gray-700" style={{marginLeft:"5px",marginTop:"2px"}}
                          onClick={() => {
    
                            handleTooltip(`${ADD_KEYS.TWO}_${index}`,'add');
                          }}
                          data-tooltip-id={`${ADD_KEYS.TWO}_${index}`}
                         ></i>
                        )
                      }

                    {

                      tooltip[`${ADD_KEYS.TWO}_${index}`] && <div className='tooltip-outer'>

                        <TooltipLite
                            isOpen={tooltip[`${ADD_KEYS.TWO}_${index}`]}
                            className=" react-tooltip-lite"
                            id={`${ADD_KEYS.TWO}_${index}`}
                            place="bottom"
                            clickable={true}
                            render={() =>
                              <div className="controlled-example tooltip-main">
                                  <div className="tooltip-data">
                                    <span className="controlled-example_close-button" onClick={() => {  handleTooltip(`${ADD_KEYS.TWO}_${index}`,'close');}}><i className="ic-clearclose"></i></span>
                                    <span className="selected-value">
                            {DROPDOWN_DATA_KEYS?.TWO
                              ?.filter((v: any) =>
                                row?.[ADD_KEYS.TWO]?.includes?.(v.value)
                              )
                              ?.map((v: any) => v.label)
                              .join(", ")}
                          </span>
                                  </div>
                              </div>
                            }

                        />
                      </div>
                    }
                    </div>
                  </fieldset>
                </td>

                <td>
                    <fieldset className="multiselect-dropDown">
                    <div className="material d-flex select-box-width-icon">
                      <MultiSelect
                        key={"dropdown"}
                        options={DROPDOWN_DATA_KEYS.THREE|| []}
                        placeholder={HEADERS?.THREE}
                        className="select-bx-table"
                        classNamePrefix="select-bx-table"
                        value={
                          Array.isArray(row?.[ADD_KEYS.THREE]) &&
                          DROPDOWN_DATA_KEYS?.THREE.length
                            ? DROPDOWN_DATA_KEYS?.THREE?.filter((v: any) =>
                                row?.[ADD_KEYS.THREE].includes(v.value)
                              )
                            : []
                        }
                        isSelectAll={true}
                        isClearable={false}
                        onChange={(e: any) =>
                          handleChange(e, ADD_KEYS.THREE, index)
                        }
                        isDisabled={row.insurer_status===INSURER_STATUS.INACTIVE?true:false}
                      />
                      {errors?.[currentPage]?.[index]?.[ADD_KEYS.THREE] ? (
                        <span className="error-txt">
                          {errors?.[currentPage]?.[index]?.[ADD_KEYS.THREE]}
                        </span>
                      ) : (
                        ""
                      )}
                      {
                        row?.[ADD_KEYS.THREE]?.length > 0 && (
                          <i
                          className="ic-info-icon1 cursor-pointer text-gray-500 hover:text-gray-700" style={{marginLeft:"5px",marginTop:"2px"}}
                          onClick={() => {
    
                            handleTooltip(`${ADD_KEYS.THREE}_${index}`,'add');
                          }}
                          data-tooltip-id={`${ADD_KEYS.THREE}_${index}`}
                         ></i>
                        )
                      }

                    {

                      tooltip[`${ADD_KEYS.THREE}_${index}`] && <div className='tooltip-outer'>

                        <TooltipLite
                            isOpen={tooltip[`${ADD_KEYS.THREE}_${index}`]}
                            className=" react-tooltip-lite"
                            id={`${ADD_KEYS.THREE}_${index}`}
                            place="bottom"
                            clickable={true}
                            render={() =>
                              <div className="controlled-example tooltip-main">
                                  <div className="tooltip-data">
                                    <span className="controlled-example_close-button" onClick={() => {  handleTooltip(`${ADD_KEYS.THREE}_${index}`,'close');}}><i className="ic-clearclose"></i></span>
                                    <span className="selected-value">
                            {DROPDOWN_DATA_KEYS?.THREE
                              ?.filter((v: any) =>
                                row?.[ADD_KEYS.THREE]?.includes?.(v.value)
                              )
                              ?.map((v: any) => v.label)
                              .join(", ")}
                          </span>
                                  </div>
                              </div>
                            }

                        />
                      </div>
                    }
                    </div>
                  </fieldset>
                </td>

                <td>
                    <fieldset className="multiselect-dropDown">
                    <div className="material d-flex select-box-width-icon">
                      <MultiSelect
                        key={"dropdown"}
                        options={row?.[ADD_KEYS.THREE] ? (DROPDOWN_DATA_KEYS.FOUR || []).filter((opt:any) =>
                          (row?.[ADD_KEYS.THREE] || []).includes(opt.category_id)
                        ):[]}
                        placeholder={HEADERS?.FOUR}
                        className="select-bx-table"
                        classNamePrefix="select-bx-table"
                        value={
                          Array.isArray(row?.[ADD_KEYS.FOUR]) &&
                          DROPDOWN_DATA_KEYS?.FOUR.length
                            ? DROPDOWN_DATA_KEYS?.FOUR?.filter((v: any) =>
                                row?.[ADD_KEYS.FOUR].includes(v.value)
                              )
                            : []
                        }
                        isSelectAll={true}
                        isClearable={false}
                        onChange={(e: any) =>
                          handleChange(e, ADD_KEYS.FOUR, index)
                        }
                        isDisabled={row.insurer_status===INSURER_STATUS.INACTIVE?true:false}
                      />
                      {errors?.[currentPage]?.[index]?.[ADD_KEYS.FOUR] ? (
                        <span className="error-txt">
                          {errors?.[currentPage]?.[index]?.[ADD_KEYS.FOUR]}
                        </span>
                      ) : (
                        ""
                      )}
                      {
                        row?.[ADD_KEYS.FOUR]?.length > 0 && (
                          <i
                          className="ic-info-icon1 cursor-pointer text-gray-500 hover:text-gray-700" style={{marginLeft:"5px",marginTop:"2px"}}
                          onClick={() => {
    
                            handleTooltip(`${ADD_KEYS.FOUR}_${index}`,'add');
                          }}
                          data-tooltip-id={`${ADD_KEYS.FOUR}_${index}`}
                         ></i>
                        )
                      }

                    {

                      tooltip[`${ADD_KEYS.FOUR}_${index}`] && <div className='tooltip-outer'>

                        <TooltipLite
                            isOpen={tooltip[`${ADD_KEYS.FOUR}_${index}`]}
                            className=" react-tooltip-lite"
                            id={`${ADD_KEYS.FOUR}_${index}`}
                            place="bottom"
                            clickable={true}
                            render={() =>
                              <div className="controlled-example tooltip-main">
                                  <div className="tooltip-data">
                                    <span className="controlled-example_close-button" onClick={() => {  handleTooltip(`${ADD_KEYS.FOUR}_${index}`,'close');}}><i className="ic-clearclose"></i></span>
                                    <span className="selected-value">
                            {DROPDOWN_DATA_KEYS?.FOUR
                              ?.filter((v: any) =>
                                row?.[ADD_KEYS.FOUR]?.includes?.(v.value)
                              )
                              ?.map((v: any) => v.label)
                              .join(", ")}
                          </span>
                                  </div>
                              </div>
                            }

                        />
                      </div>
                    }
                    </div>
                  </fieldset>
                </td>

                <td>
                    <fieldset className="multiselect-dropDown">
                    <div className="material d-flex select-box-width-icon">
                      <MultiSelect
                        key={"dropdown"}
                        options={DROPDOWN_DATA_KEYS.FIVE|| []}
                        placeholder={HEADERS?.FIVE}
                        className="select-bx-table"
                        classNamePrefix="select-bx-table"
                        value={
                          Array.isArray(row?.[ADD_KEYS.FIVE]) &&
                          DROPDOWN_DATA_KEYS?.FIVE.length
                            ? DROPDOWN_DATA_KEYS?.FIVE?.filter((v: any) =>
                                row?.[ADD_KEYS.FIVE].includes(v.value)
                              )
                            : []
                        }
                        isSelectAll={true}
                        isClearable={false}
                        onChange={(e: any) =>
                          handleChange(e, ADD_KEYS.FIVE, index)
                        }
                        isDisabled={row.insurer_status===INSURER_STATUS.INACTIVE?true:false}
                      />
                      {errors?.[currentPage]?.[index]?.[ADD_KEYS.FIVE] ? (
                        <span className="error-txt">
                          {errors?.[currentPage]?.[index]?.[ADD_KEYS.FIVE]}
                        </span>
                      ) : (
                        ""
                      )}
                      {
                        row?.[ADD_KEYS.FIVE]?.length > 0 && (
                          <i
                          className="ic-info-icon1 cursor-pointer text-gray-500 hover:text-gray-700" style={{marginLeft:"5px",marginTop:"2px"}}
                          onClick={() => {
    
                            handleTooltip(`${ADD_KEYS.FIVE}_${index}`,'add');
                          }}
                          data-tooltip-id={`${ADD_KEYS.FIVE}_${index}`}
                         ></i>
                        )
                      }

                    {

                      tooltip[`${ADD_KEYS.FIVE}_${index}`] && <div className='tooltip-outer'>

                        <TooltipLite
                            isOpen={tooltip[`${ADD_KEYS.FIVE}_${index}`]}
                            className=" react-tooltip-lite"
                            id={`${ADD_KEYS.FIVE}_${index}`}
                            place="bottom"
                            clickable={true}
                            render={() =>
                              <div className="controlled-example tooltip-main">
                                  <div className="tooltip-data">
                                    <span className="controlled-example_close-button" onClick={() => {  handleTooltip(`${ADD_KEYS.FIVE}_${index}`,'close');}}><i className="ic-clearclose"></i></span>
                                    <span className="selected-value">
                            {DROPDOWN_DATA_KEYS?.FIVE
                              ?.filter((v: any) =>
                                row?.[ADD_KEYS.FIVE]?.includes?.(v.value)
                              )
                              ?.map((v: any) => v.label)
                              .join(", ")}
                          </span>
                                  </div>
                              </div>
                            }

                        />
                      </div>
                    }
                    </div>
                  </fieldset>
                </td>

                <td>
                    <fieldset className="multiselect-dropDown">
                    <div className="material d-flex select-box-width-icon">
                      <MultiSelect
                        key={"dropdown"}
                        options={DROPDOWN_DATA_KEYS.SIX|| []}
                        placeholder={HEADERS?.SIX}
                        className="select-bx-table"
                        classNamePrefix="select-bx-table"
                        value={
                          Array.isArray(row?.[ADD_KEYS.SIX]) &&
                          DROPDOWN_DATA_KEYS?.SIX.length
                            ? DROPDOWN_DATA_KEYS?.SIX?.filter((v: any) =>
                                row?.[ADD_KEYS.SIX].includes(v.value)
                              )
                            : []
                        }
                        isSelectAll={true}
                        isClearable={false}
                        onChange={(e: any) =>
                          handleChange(e, ADD_KEYS.SIX, index)
                        }
                        isDisabled={row.insurer_status===INSURER_STATUS.INACTIVE?true:false}
                      />
                      {errors?.[currentPage]?.[index]?.[ADD_KEYS.SIX] ? (
                        <span className="error-txt">
                          {errors?.[currentPage]?.[index]?.[ADD_KEYS.SIX]}
                        </span>
                      ) : (
                        ""
                      )}
                      {
                        row?.[ADD_KEYS.SIX]?.length > 0 && (
                          <i
                          className="ic-info-icon1 cursor-pointer text-gray-500 hover:text-gray-700" style={{marginLeft:"5px",marginTop:"2px"}}
                          onClick={() => {
    
                            handleTooltip(`${ADD_KEYS.SIX}_${index}`,'add');
                          }}
                          data-tooltip-id={`${ADD_KEYS.SIX}_${index}`}
                         ></i>
                        )
                      }

                    {

                      tooltip[`${ADD_KEYS.SIX}_${index}`] && <div className='tooltip-outer'>

                        <TooltipLite
                            isOpen={tooltip[`${ADD_KEYS.SIX}_${index}`]}
                            className=" react-tooltip-lite"
                            id={`${ADD_KEYS.SIX}_${index}`}
                            place="bottom"
                            clickable={true}
                            render={() =>
                              <div className="controlled-example tooltip-main">
                                  <div className="tooltip-data">
                                    <span className="controlled-example_close-button" onClick={() => {  handleTooltip(`${ADD_KEYS.SIX}_${index}`,'close');}}><i className="ic-clearclose"></i></span>
                                    <span className="selected-value">
                            {DROPDOWN_DATA_KEYS?.SIX
                              ?.filter((v: any) =>
                                row?.[ADD_KEYS.SIX]?.includes?.(v.value)
                              )
                              ?.map((v: any) => v.label)
                              .join(", ")}
                          </span>
                                  </div>
                              </div>
                            }

                        />
                      </div>
                    }
                    </div>
                  </fieldset>
                </td>

                <td>
                  <fieldset className="single-select">
                    <div className="material">
                      <ReactSelect
                        options={DROPDOWN_DATA_KEYS?.SEVEN || []}
                        placeholder={HEADERS?.SEVEN}
                        className="select-bx-table"
                        classNamePrefix="select-bx-table"
                        value={
                          row?.[ADD_KEYS.SEVEN]
                            ? DROPDOWN_DATA_KEYS?.SEVEN
                                ?.find(
                                  (item: any) => +item.value === +row?.[ADD_KEYS.SEVEN]
                                )
                            : null
                        }
                        handleChange={(e: any) =>
                          handleChange(e, ADD_KEYS.SEVEN, index)
                        }
                      />
                      {errors?.[currentPage]?.[index]?.[ADD_KEYS.SEVEN] ? (
                        <span className="error-txt">
                          {errors?.[currentPage]?.[index]?.[ADD_KEYS.SEVEN]}
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </fieldset>
                </td>

                <td>
                  <fieldset>
                    <div className="material">
                      <input
                        type="number"
                        className="form-input"
                        placeholder=" "
                        // value={row?.[ADD_KEYS.EIGHT] ?? ""}
                        value={FIXED_FIRST_YEAR_DEPRECIATION_RATE}
                        onChange={(e: any) =>
                          handleChange(e, ADD_KEYS.EIGHT, index)
                        }
                        onKeyDown={(e) => restrictInvalidNumberKeys(e,["."])}
                        disabled={true}
                      />
                      {errors?.[currentPage]?.[index]
                        ?.[ADD_KEYS.EIGHT] ? (
                        <span className="error-txt multiten-error">
                          {
                            errors?.[currentPage]?.[index]
                              ?.[ADD_KEYS.EIGHT]
                          }
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </fieldset>
                </td>


                <td>
                  <fieldset>
                    <div className="material">
                      <input
                        type="number"
                        className="form-input"
                        placeholder=" "
                        value={row?.[ADD_KEYS.NINE] ?? ""}
                        onChange={(e: any) =>
                          handleChange(
                            e,
                            ADD_KEYS.NINE,
                            index
                          )
                        }
                        onKeyDown={(e) => restrictInvalidNumberKeys(e,["."])}
                        disabled={isDepreciationRateDisabled(row, 2)}
                      />
                      {errors?.[currentPage]?.[index]
                        ?.[ADD_KEYS.NINE] ? (
                        <span className="error-txt multiten-error">
                          {
                            errors?.[currentPage]?.[index]
                              ?.[ADD_KEYS.NINE]
                          }
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </fieldset>
                </td>


                  <td>
                  <fieldset>
                    <div className="material">
                      <input
                        type="number"
                        className="form-input"
                        placeholder=" "
                        value={row?.[ADD_KEYS.TEN] ?? ""}
                        onChange={(e: any) =>
                          handleChange(
                            e,
                            ADD_KEYS.TEN,
                            index
                          )
                        }
                        onKeyDown={(e) => restrictInvalidNumberKeys(e,["."])}
                        disabled={isDepreciationRateDisabled(row, 3)}
                      />
                      {errors?.[currentPage]?.[index]
                        ?.[ADD_KEYS.TEN] ? (
                        <span className="error-txt multiten-error">
                          {
                            errors?.[currentPage]?.[index]
                              ?.[ADD_KEYS.TEN]
                          }
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </fieldset>
                </td>

                  <td>
                  <fieldset>
                    <div className="material">
                      <input
                        type="number"
                        className="form-input"
                        placeholder=" "
                        value={row?.[ADD_KEYS.ELEVEN] ?? ""}
                        onChange={(e: any) =>
                          handleChange(
                            e,
                            ADD_KEYS.ELEVEN,
                            index
                          )
                        }
                        onKeyDown={(e) => restrictInvalidNumberKeys(e,["."])}
                        disabled={isDepreciationRateDisabled(row, 4)}
                      />
                      {errors?.[currentPage]?.[index]
                        ?.[ADD_KEYS.ELEVEN] ? (
                        <span className="error-txt multiten-error">
                          {
                            errors?.[currentPage]?.[index]
                              ?.[ADD_KEYS.ELEVEN]
                          }
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </fieldset>
                </td>

                  <td>
                  <fieldset>
                    <div className="material">
                      <input
                        type="number"
                        className="form-input"
                        placeholder=" "
                        value={row?.[ADD_KEYS.TWELVE] ?? ""}
                        onChange={(e: any) =>
                          handleChange(
                            e,
                            ADD_KEYS.TWELVE,
                            index
                          )
                        }
                        onKeyDown={(e) => restrictInvalidNumberKeys(e,["."])}
                        disabled={isDepreciationRateDisabled(row, 5)}
                      />
                      {errors?.[currentPage]?.[index]
                        ?.[ADD_KEYS.TWELVE] ? (
                        <span className="error-txt multiten-error">
                          {
                            errors?.[currentPage]?.[index]
                              ?.[ADD_KEYS.TWELVE]
                          }
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </fieldset>
                </td>
              </tr>
            ))}
            {addRowEnabled ? (
              <tr>
                <td className="p-tb-0" colSpan={12}>
                  <a
                    href="#!"
                    onClick={(e) => {
                      e.preventDefault();
                      addRow();
                    }}
                  >
                    <i className="ic-add m-xs-r"></i>{" "}
                    {translations?.ADDONS?.COMMON?.ADD_ROW ||
                      translations?.BASIC_PREMIUM?.ADD_ROW ||
                      "Add Row"}
                  </a>
                </td>
              </tr>
            ) : (
              ""
            )}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
}
export default React.memo(MultiTenure)
